import React from 'react';
import { Paper, Typography, Chip, Box } from '@mui/material';

export interface EvaluationGoalReference {
  target_description: string;
  achievement_criteria: string;
  approval_status: string;
  rejection_reason?: string | null;
  revision_phase?: string;
  version?: number;
}

const statusLabel: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { label: '下書き', color: 'default' },
  pending_approval: { label: '承認待ち', color: 'warning' },
  approved: { label: '承認済み', color: 'success' },
  rejected: { label: '差し戻し', color: 'error' },
};

interface GoalReferencePanelProps {
  goal: EvaluationGoalReference | null | undefined;
  title?: string;
  /**
   * 項目別の目標を画面内で表示しているか。
   * true のとき本パネルは承認状態のみを示す。`evaluation_goals` の内容はフリー項目の
   * 同期値であり、これを「目標」として出すと項目別目標と二重になり誤解を生む。
   * 項目が未生成の評価（項目別化より前のデータ）では false を渡し、従来の単一目標表示へ
   * フォールバックする。
   */
  itemGoalsShown?: boolean;
}

export const GoalReferencePanel: React.FC<GoalReferencePanelProps> = ({
  goal,
  title = '被評価者の目標・達成基準',
  itemGoalsShown = false,
}) => {
  if (!goal || goal.approval_status === 'draft') {
    return null;
  }

  const status = statusLabel[goal.approval_status] ?? {
    label: goal.approval_status,
    color: 'default' as const,
  };

  const hasContent =
    (goal.target_description?.trim() ?? '') !== '' ||
    (goal.achievement_criteria?.trim() ?? '') !== '';

  if (!hasContent && !itemGoalsShown) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6">{title}</Typography>
        <Chip size="small" label={status.label} color={status.color} />
        {goal.revision_phase && (
          <Chip
            size="small"
            variant="outlined"
            label={goal.revision_phase === 'mid_term' ? '中期改訂' : '期初'}
          />
        )}
        {goal.version != null && (
          <Typography variant="caption" color="text.secondary">
            v{goal.version}
          </Typography>
        )}
      </Box>
      {goal.approval_status === 'rejected' && goal.rejection_reason && (
        <Typography variant="body2" color="error" paragraph>
          差し戻し理由: {goal.rejection_reason}
        </Typography>
      )}
      {itemGoalsShown ? (
        <Typography variant="body2" color="text.secondary">
          目標と達成基準は評価項目ごとに設定されます。内容は各項目の欄に表示しています。
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            目標設定（SMART）
          </Typography>
          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {goal.target_description || '（未入力）'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            達成基準
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {goal.achievement_criteria || '（未入力）'}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default GoalReferencePanel;
